import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useViewAdminRestaurants } from "@dashboard/components/admin/queries";
import type { RawRestDetails } from "@dashboard/components/admin/queries";
import { useDashboardAppFeatureFlag } from "@dashboard/hooks/useFeature";
import { restaurantSelectedByAdminAction } from "@dashboard/store/actions/restaurantActions";
import type { RestaurantReducerDispatch } from "@dashboard/store/actions/restaurantActions";
import { restaurantFromDoc } from "@dashboard/store/backendApis/restaurantApi";
import type { ReduxState } from "@dashboard/types";

import { WorkspaceSwitcherView } from "./WorkspaceSwitcherView";
import type { WorkspaceOption } from "./workspaceSections";

function optionFromRaw(raw: RawRestDetails): WorkspaceOption {
  return {
    uuid: raw.restaurant.uuid,
    name: raw.restaurant.name,
    env: raw.restaurant.env,
    city: raw.restaurant.address?.city,
    isRecentlyViewed: raw.isRecentlyViewed,
  };
}

export const WorkspaceSwitcher = ({
  open,
  onClose,
  anchorEl,
}: {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
}) => {
  const dispatch: RestaurantReducerDispatch = useDispatch();
  const selectedRestaurant = useSelector(
    (state: ReduxState) => state.restaurant.selectedRestaurant
  );
  const showIds = useDashboardAppFeatureFlag("showIds");
  const {
    viewData: { data: rawRestaurants = [], isLoading },
    error,
    onView,
  } = useViewAdminRestaurants();

  const { options, rawByUuid } = useMemo(() => {
    const byUuid = new Map<string, RawRestDetails>();
    const mapped = rawRestaurants.map((raw) => {
      byUuid.set(raw.restaurant.uuid, raw);
      return optionFromRaw(raw);
    });
    return { options: mapped, rawByUuid: byUuid };
  }, [rawRestaurants]);

  function switchToWorkspace(option: WorkspaceOption) {
    const raw = rawByUuid.get(option.uuid);
    if (!raw) {
      return;
    }
    dispatch(
      restaurantSelectedByAdminAction(
        restaurantFromDoc(raw.restaurant),
        raw.paymentPlan
      )
    );
    // Best-effort history write; not awaited so the switch stays instant, and
    // a failed write must not surface as an unhandled rejection.
    onView(raw.restaurant.uuid).catch(() => {});
    onClose();
  }

  return (
    <WorkspaceSwitcherView
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      options={options}
      currentWorkspaceUuid={selectedRestaurant?.uuid}
      loading={isLoading}
      error={error}
      showIds={showIds}
      onSelect={switchToWorkspace}
    />
  );
};
